import { useCallback, useEffect, useRef } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { useAuthStore } from '../stores';

// Check if Privy is configured
const PRIVY_ENABLED = Boolean(import.meta.env.VITE_PRIVY_APP_ID);

interface User {
  id: string;
  email?: string;
  walletAddress?: string;
  displayName?: string;
  avatar?: string;
}

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: () => void;
  logout: () => void;
  clearError: () => void;
  devMode: boolean;
  privyEnabled: boolean;
  privyUser: unknown;
  privyReady: boolean;
}

export function useAuth(): UseAuthReturn {
  // Always call usePrivy unconditionally at the top level (React hooks rule)
  // When PRIVY_ENABLED is false, the app structure ensures PrivyProvider still exists
  // but in a minimal mode, so this hook is safe to call
  const privyResult = usePrivy();

  // Use the result only when Privy is actually configured
  const privy = PRIVY_ENABLED
    ? privyResult
    : {
        ready: true,
        authenticated: false,
        user: null,
        login: () => console.warn('Privy not configured'),
        logout: async () => {},
        getAccessToken: async () => null,
      };

  const {
    user,
    isAuthenticated,
    isLoading: isAuthLoading,
    error,
    devMode,
    loginWithPrivy,
    loginDev,
    logout: storeLogout,
    checkAuth,
    clearError,
  } = useAuthStore();

  // Track sync attempts to prevent infinite loops
  const syncAttemptedRef = useRef(false);
  const isSyncingRef = useRef(false);

  // Check auth on mount - only run once
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync Privy auth state with our backend
  // Only attempt once when privy becomes authenticated
  useEffect(() => {
    if (!PRIVY_ENABLED) return;

    // Skip if already authenticated, already attempted, or currently syncing
    if (isAuthenticated || syncAttemptedRef.current || isSyncingRef.current) {
      return;
    }

    // Only sync when privy is ready and authenticated
    if (!privy.ready || !privy.authenticated || !privy.user) {
      return;
    }

    const syncAuth = async (): Promise<void> => {
      isSyncingRef.current = true;
      syncAttemptedRef.current = true;

      try {
        const token = await privy.getAccessToken();
        if (token) {
          await loginWithPrivy(token);
        }
      } catch (err) {
        console.error('Failed to sync auth with backend:', err);
        // Don't retry on failure - user can manually login again
      } finally {
        isSyncingRef.current = false;
      }
    };

    syncAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privy.ready, privy.authenticated, privy.user, isAuthenticated, loginWithPrivy]);

  // Reset sync attempt when user logs out (privy becomes unauthenticated)
  useEffect(() => {
    if (!privy.authenticated) {
      syncAttemptedRef.current = false;
    }
  }, [privy.authenticated]);

  const login = useCallback(() => {
    if (devMode) {
      loginDev();
    } else {
      privy.login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privy.login, devMode, loginDev]);

  const logout = useCallback(() => {
    storeLogout();
    if (PRIVY_ENABLED) {
      privy.logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeLogout, privy.logout]);

  return {
    user,
    isAuthenticated,
    isLoading: isAuthLoading || !privy.ready,
    error,
    login,
    logout,
    clearError,
    devMode,
    privyEnabled: PRIVY_ENABLED,
    privyUser: privy.user,
    privyReady: privy.ready,
  };
}
